/**
 * Avery sheet PDF generation. Renders Code 128 barcodes via bwip-js → PNG →
 * jsPDF `addImage`. Pure client-side path per Spike 1.3 (thermal goes through
 * the Python service; only sheet PDFs stay in the browser).
 */

import jsPDF from 'jspdf';
import { fitLines, fitText } from './fit-text';
import bwipjs from 'bwip-js/browser';
import type { AverySheetSpec } from './specs';

export interface LabelItem {
  barcode: string;
  line1: string;
  line2?: string;
  /** Pre-fetched data URL (e.g. `data:image/jpeg;base64,...`). Embedded in the
   *  upper-right corner of large labels (labelHeightIn ≥ 1.5"). */
  imageUrl?: string;
  /** Card name used for thumbnail lookup (e.g. Scryfall). When set, the drawer
   *  uses this instead of `line1` so that `line1` can carry the barcode string. */
  cardName?: string;
}

const PT_PER_IN = 72;

export function buildLabelSheetPDF(items: LabelItem[], spec: AverySheetSpec): jsPDF {
  const doc = new jsPDF({
    unit: 'pt',
    format: [spec.pageWidthIn * PT_PER_IN, spec.pageHeightIn * PT_PER_IN],
  });

  const labelsPerPage = spec.rows * spec.cols;
  const labelWidthPt = spec.labelWidthIn * PT_PER_IN;
  const labelHeightPt = spec.labelHeightIn * PT_PER_IN;
  const marginTopPt = spec.marginTopIn * PT_PER_IN;
  const marginLeftPt = spec.marginLeftIn * PT_PER_IN;
  const colGapPt = spec.colGapIn * PT_PER_IN;
  const rowGapPt = spec.rowGapIn * PT_PER_IN;

  // Thumbnail column occupies the rightmost 28% on labels taller than 1.5 inches.
  const hasThumbnailRoom = spec.labelHeightIn >= 1.5;
  const thumbWidthPt = hasThumbnailRoom ? Math.round(labelWidthPt * 0.28) : 0;
  const contentWidthPt = labelWidthPt - thumbWidthPt;

  items.forEach((item, idx) => {
    if (idx > 0 && idx % labelsPerPage === 0) doc.addPage();
    const pageIdx = idx % labelsPerPage;
    const row = Math.floor(pageIdx / spec.cols);
    const col = pageIdx % spec.cols;
    const x = marginLeftPt + col * (labelWidthPt + colGapPt);
    const y = marginTopPt + row * (labelHeightPt + rowGapPt);

    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      try {
        bwipjs.toCanvas(canvas, {
          bcid: 'code128',
          text: item.barcode,
          scale: 2,
          height: 10,
          includetext: false,
        });
        const dataUrl = canvas.toDataURL('image/png');
        doc.addImage(dataUrl, 'PNG', x + 4, y + 4, contentWidthPt - 8, labelHeightPt * 0.55);
      } catch {
        // Fall back to text-only label if bwip-js fails (e.g. invalid barcode chars)
      }
    }

    // Embed card thumbnail in upper-right when a pre-fetched data URL is provided.
    if (hasThumbnailRoom && item.imageUrl) {
      try {
        const imgX = x + contentWidthPt + 2;
        const imgY = y + 4;
        doc.addImage(item.imageUrl, 'JPEG', imgX, imgY, thumbWidthPt - 4, labelHeightPt - 8);
      } catch {
        // Skip silently if the data URL format is unrecognised
      }
    }

    /*
     * Fitted, not wrapped — vendor#1103, the same defect the thermal builder
     * carried. `{ maxWidth }` wraps rather than truncating, and `line2`'s
     * baseline sits 4pt above the bottom edge, so an overlong second line put
     * its remainder off the label. On a sheet that means it lands on the label
     * BELOW, which is worse than clipping: the wrong card's sticker gets a
     * stray line of someone else's text.
     */
    const textMaxWidth = contentWidthPt - 8;
    const ref = fitText(doc, item.line1, textMaxWidth, { max: 8, min: 6.5 });
    // A second card line before a lost character — vendor#1105. Drawn upwards
    // from the bottom baseline so an extra line grows INTO the label rather
    // than onto the one below it.
    const card = item.line2
      ? fitLines(doc, item.line2, textMaxWidth, {
          max: 8,
          min: 5.5,
          singleLineMin: 6.5,
          maxLines: 2,
        })
      : null;

    doc.setFontSize(ref.fontSize);
    doc.text(ref.text, x + 4, y + labelHeightPt - 14);
    if (card) {
      doc.setFontSize(card.fontSize);
      let cy = y + labelHeightPt - 4;
      for (const line of [...card.lines].reverse()) {
        doc.text(line, x + 4, cy);
        cy -= card.fontSize * 1.25;
      }
    }
    doc.setFontSize(8);
  });

  return doc;
}
